import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, ElementRef, ViewChild, inject, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { Router } from '@angular/router';
import { initFlowbite } from 'flowbite';
import { finalize } from 'rxjs/operators';
import { PopupMessageComponent } from '../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../components/spinner/spinner.component';
import { PHYSICAL_SECURITY_FORM } from '../../../shared/definitions/physical-security.definition';
import {
  PhysicalFieldDefinition,
  PhysicalFieldOption,
  PhysicalSectionDefinition
} from '../../../shared/models/form-definition';
import { EFormType } from '../../../shared/models/types.enum';
import { FormsService } from '../../../shared/services/forms.service';
import { UserDataService } from '../../../shared/services/userData.service';

@Component({
  selector: 'app-physical-security-form',
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    SpinnerComponent, 
    PopupMessageComponent
  ],
  templateUrl: './physical-security-form.component.html'
})
export class PhysicalSecurityFormComponent {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly formsService = inject(FormsService);
  private readonly userDataService = inject(UserDataService);
  @ViewChild('scrollContainer') private readonly scrollContainer?: ElementRef<HTMLDivElement>;
  readonly definition = PHYSICAL_SECURITY_FORM;
  readonly form = this.buildForm();
  readonly states = [
    { label: 'Alabama', value: 'AL' },
    { label: 'Alaska', value: 'AK' },
    { label: 'Arizona', value: 'AZ' },
    { label: 'Arkansas', value: 'AR' },
    { label: 'California', value: 'CA' },
    { label: 'Colorado', value: 'CO' },
    { label: 'Connecticut', value: 'CT' },
    { label: 'Delaware', value: 'DE' },
    { label: 'District of Columbia', value: 'DC' },
    { label: 'Florida', value: 'FL' },
    { label: 'Georgia', value: 'GA' },
    { label: 'Hawaii', value: 'HI' },
    { label: 'Idaho', value: 'ID' },
    { label: 'Illinois', value: 'IL' },
    { label: 'Indiana', value: 'IN' },
    { label: 'Iowa', value: 'IA' },
    { label: 'Kansas', value: 'KS' },
    { label: 'Kentucky', value: 'KY' },
    { label: 'Louisiana', value: 'LA' },
    { label: 'Maine', value: 'ME' },
    { label: 'Maryland', value: 'MD' },
    { label: 'Massachusetts', value: 'MA' },
    { label: 'Michigan', value: 'MI' },
    { label: 'Minnesota', value: 'MN' },
    { label: 'Mississippi', value: 'MS' },
    { label: 'Missouri', value: 'MO' },
    { label: 'Montana', value: 'MT' },
    { label: 'Nebraska', value: 'NE' },
    { label: 'Nevada', value: 'NV' },
    { label: 'New Hampshire', value: 'NH' },
    { label: 'New Jersey', value: 'NJ' },
    { label: 'New Mexico', value: 'NM' },
    { label: 'New York', value: 'NY' },
    { label: 'North Carolina', value: 'NC' },
    { label: 'North Dakota', value: 'ND' },
    { label: 'Ohio', value: 'OH' },
    { label: 'Oklahoma', value: 'OK' },
    { label: 'Oregon', value: 'OR' },
    { label: 'Pennsylvania', value: 'PA' },
    { label: 'Rhode Island', value: 'RI' },
    { label: 'South Carolina', value: 'SC' },
    { label: 'South Dakota', value: 'SD' },
    { label: 'Tennessee', value: 'TN' },
    { label: 'Texas', value: 'TX' },
    { label: 'Utah', value: 'UT' },
    { label: 'Vermont', value: 'VT' },
    { label: 'Virginia', value: 'VA' },
    { label: 'Washington', value: 'WA' },
    { label: 'West Virginia', value: 'WV' },
    { label: 'Wisconsin', value: 'WI' },
    { label: 'Wyoming', value: 'WY' }
  ];
  currentSectionIndex = 0;
  readonly loading = signal(false);
  readonly messageModal = signal<string | null>(null);
  readonly popupMessage = signal<string | null>(null);

  constructor() {
    this.attachNumericFormatting();
    this.refreshTooltips();
  }

  get currentSection(): PhysicalSectionDefinition {
    return this.definition.sections[this.currentSectionIndex];
  }

  get isFirstSection(): boolean {
    return this.currentSectionIndex === 0;
  }

  get isLastSection(): boolean {
    return this.currentSectionIndex === this.definition.sections.length - 1;
  }

  get currentSectionProgressLabel(): string {
    return `${this.currentSectionIndex + 1} / ${this.definition.sections.length}`;
  }

  getVisibleFields(section: PhysicalSectionDefinition): PhysicalFieldDefinition[] {
    return section.fields.filter((field) => this.isFieldVisible(field));
  }

  getFieldControl(key: string): AbstractControl {
    return this.form.controls[key];
  }

  getTextControl(key: string): FormControl {
    return this.getFieldControl(key) as FormControl;
  }

  getRadioOptions(field: PhysicalFieldDefinition): PhysicalFieldOption[] {
    return field.options ?? [];
  }

  getOptionLabel(field: PhysicalFieldDefinition, value: string): string {
    return this.getOptionByControlKey(field, value)?.label ?? this.formatOptionValue(value);
  }

  getCheckboxGroup(key: string): FormGroup {
    return this.getFieldControl(key) as FormGroup;
  }

  getAddressGroup(key: string): FormGroup {
    return this.getFieldControl(key) as FormGroup;
  }

  getRepeatArray(key: string): FormArray {
    return this.getFieldControl(key) as FormArray;
  }

  handlePhoneInput(key: string, event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const formatted = this.formatPhoneNumber(input?.value ?? '');
    this.getTextControl(key).setValue(formatted, { emitEvent: false });
    if (input) {
      input.value = formatted;
    }
  }

  addRepeatItem(key: string, initialValue = ''): void {
    this.getRepeatArray(key).push(new FormControl(initialValue, Validators.required));
  }

  removeRepeatItem(key: string, index: number): void {
    this.getRepeatArray(key).removeAt(index);
  }

  checkboxOptionKeys(field: PhysicalFieldDefinition): string[] {
    return (field.options ?? []).map((option) => this.toCheckboxControlKey(option.value));
  }

  formatOptionValue(value: string): string {
    return value.replace(/_/g, ' ');
  }

  canProceedFromSection(section: PhysicalSectionDefinition): boolean {
    return this.getVisibleFields(section).every((field) => this.isFieldSatisfied(field));
  }

  canMoveToSection(sectionIndex: number): boolean {
    if (sectionIndex <= this.currentSectionIndex) {
      return true;
    }

    return this.definition.sections
      .slice(0, sectionIndex)
      .every((section) => this.canProceedFromSection(section));
  }

  goToSection(sectionIndex: number): void {
    if (!this.canMoveToSection(sectionIndex)) {
      return;
    }

    this.currentSectionIndex = sectionIndex;
    this.scrollToTop();
    this.refreshTooltips();
  }

  previousSection(): void {
    if (this.isFirstSection) {
      return;
    }

    this.currentSectionIndex -= 1;
    this.scrollToTop();
    this.refreshTooltips();
  }

  nextSection(): void {
    if (this.isLastSection || !this.canProceedFromSection(this.currentSection)) {
      return;
    }

    this.currentSectionIndex += 1;
    this.scrollToTop();
    this.refreshTooltips();
  }

  onBack(): void {
    const buildingId = this.userDataService.getBuildingId();
    if (!buildingId) {
      return;
    }

    this.router.navigate(['/building', buildingId, 'form-dashboard']);
  }

  submit(): void {
    const data: Record<string, unknown> = {};

    for (const section of this.definition.sections) {
      for (const field of section.fields) {
        const visible = this.isFieldVisible(field);
        const control = this.getFieldControl(field.key);

        switch (field.type) {
          case 'repeat': {
            const items = visible
              ? (control as FormArray).getRawValue().filter((value: string) => value?.trim())
              : [];
            data[field.key] = null;
            data[`${field.key}_array`] = items;
            break;
          }
          case 'multicheckbox': {
            const selected: Record<string, boolean> = {};
            if (visible) {
              const value = (control as FormGroup).getRawValue() as Record<string, boolean>;
              for (const [optionKey, checked] of Object.entries(value)) {
                if (checked) {
                  const originalOptionValue = this.getOriginalCheckboxOptionValue(field, optionKey);
                  selected[originalOptionValue] = true;
                }
              }
            }
            data[field.key] = selected;
            break;
          }
          case 'address': {
            const value = visible
              ? this.serializeAddress(control as FormGroup)
              : { address_one: '', address_two: '', city: '', state: '', zipcode: '' };
            data[field.key] = value;
            break;
          }
          default: {
            data[field.key] = visible ? this.serializeScalarField(field.key, control.getRawValue()) : null;
            break;
          }
        }
      }
    }

    const buildingId = this.userDataService.getBuildingId();
    if (!buildingId) {
      this.messageModal.set('unsuccess');
      this.popupMessage.set('Missing building ID.');
      return;
    }

    this.loading.set(true);
    this.formsService.submitForm({
      urlParams: {
        buildingId,
        formType: EFormType.PhysicalSecurity
      },
      body: data
    }).pipe(
      finalize(() => {
        this.loading.set(false);
      })
    ).subscribe({
      next: (response) => {
        this.messageModal.set('success');
        this.popupMessage.set('Submit successful!');
        this.router.navigate(['/building', buildingId, 'form-dashboard']);
      },
      error: (error) => {
        this.messageModal.set('unsuccess');
        this.popupMessage.set(error.message);
        console.error('Error submitting physical security form:', error.message);
      }
    });
  }

  resetMessageModal(): void {
    this.messageModal.set(null);
    this.popupMessage.set(null);
  }

  private buildForm(): FormGroup {
    const controls: Record<string, AbstractControl> = {};

    for (const section of this.definition.sections) {
      for (const field of section.fields) {
        controls[field.key] = this.buildControl(field);
      }
    }

    return new FormGroup(controls);
  }

  private buildControl(field: PhysicalFieldDefinition): AbstractControl {
    switch (field.type) {
      case 'address':
        return new FormGroup({
          address_one: new FormControl('', field.required ? Validators.required : []),
          address_two: new FormControl(''),
          city: new FormControl('', field.required ? Validators.required : []),
          state: new FormControl('', field.required ? Validators.required : []),
          zipcode: new FormControl('', field.required ? Validators.required : [])
        });
      case 'multicheckbox': {
        const controls: Record<string, FormControl<boolean>> = {};
        for (const option of field.options ?? []) {
          controls[this.toCheckboxControlKey(option.value)] = new FormControl(false, { nonNullable: true });
        }
        return new FormGroup(controls);
      }
      case 'repeat':
        return new FormArray([]);
      default:
        return new FormControl('', field.required ? Validators.required : []);
    }
  }

  private attachNumericFormatting(): void {
    this.attachPhoneFormatting('school_poc_number');
    this.attachZipFormatting('school_address');
    this.attachZipFormatting('address_LE');
  }

  private attachPhoneFormatting(key: string): void {
    const control = this.getTextControl(key);
    control.valueChanges.subscribe((value) => {
      const formatted = this.formatPhoneNumber(value ?? '');
      if (formatted !== value) {
        control.setValue(formatted, { emitEvent: false });
      }
    });
  }

  private attachZipFormatting(addressKey: string): void {
    const zipcodeControl = this.getAddressGroup(addressKey).controls['zipcode'] as FormControl;
    zipcodeControl.valueChanges.subscribe((value) => {
      const digits = this.onlyDigits(value ?? '').slice(0, 9);
      if (digits !== value) {
        zipcodeControl.setValue(digits, { emitEvent: false });
      }
    });
  }

  private formatPhoneNumber(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 10);
    if (digits.length === 0) {
      return '';
    }
    if (digits.length <= 3) {
      return `(${digits}`;
    }
    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  private onlyDigits(value: string): string {
    return value.replace(/\D+/g, '');
  }

  private serializeScalarField(key: string, value: unknown): unknown {
    if (key === 'school_poc_number' && typeof value === 'string') {
      return this.onlyDigits(value);
    }

    return value;
  }

  private serializeAddress(control: FormGroup): Record<string, string> {
    const value = control.getRawValue() as Record<string, string>;

    return {
      address_one: value['address_one'] ?? '',
      address_two: value['address_two'] ?? '',
      city: value['city'] ?? '',
      state: value['state'] ?? '',
      zipcode: this.onlyDigits(value['zipcode'] ?? '')
    };
  }

  private isFieldSatisfied(field: PhysicalFieldDefinition): boolean {
    if (!field.required || !this.isFieldVisible(field)) {
      return true;
    }

    const control = this.getFieldControl(field.key);

    switch (field.type) {
      case 'multicheckbox': {
        const values = (control as FormGroup).getRawValue() as Record<string, boolean>;
        return Object.values(values).some(Boolean);
      }
      case 'repeat': {
        const values = (control as FormArray).getRawValue() as string[];
        return values.some((value) => value?.trim().length > 0);
      }
      case 'address': {
        const value = (control as FormGroup).getRawValue() as Record<string, string>;
        return ['address_one', 'city', 'state', 'zipcode'].every((key) => value[key]?.trim().length > 0);
      }
      default: {
        const value = control.getRawValue();
        return typeof value === 'string' ? value.trim().length > 0 : value !== null && value !== undefined;
      }
    }
  }

  private isFieldVisible(field: PhysicalFieldDefinition): boolean {
    if (!field.hideExpression) {
      return true;
    }

    return !this.evaluateHideExpression(field.hideExpression);
  }

  private evaluateHideExpression(expression: string): boolean {
    const model = this.buildExpressionModel();

    const notEqualsMatch = expression.match(/^model\.([A-Za-z0-9_]+)\s*!==\s*'(.+)'$/);
    if (notEqualsMatch) {
      const [, key, expected] = notEqualsMatch;
      return (model[key] as string | undefined) !== expected;
    }

    const objectPropertyMatch = expression.match(/^model\.([A-Za-z0-9_]+)\?\.\['(.+)'\]\s*!==\s*true$/);
    if (objectPropertyMatch) {
      const [, key, property] = objectPropertyMatch;
      const value = model[key] as Record<string, boolean> | undefined;
      return value?.[property] !== true;
    }

    const includesMatch = expression.match(/^!model\.([A-Za-z0-9_]+)\?\.includes\('(.+)'\)$/);
    if (includesMatch) {
      const [, key, expected] = includesMatch;
      const value = model[key];
      if (Array.isArray(value)) {
        return !value.includes(expected);
      }
      if (typeof value === 'string') {
        return !value.includes(expected);
      }
      return true;
    }

    return false;
  }

  private scrollToTop(): void {
    this.scrollContainer?.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    this.document.defaultView?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private refreshTooltips(): void {
    this.document.defaultView?.setTimeout(() => {
      initFlowbite();
    }, 0);
  }

  private toCheckboxControlKey(value: string): string {
    return value
      .replace(/\./g, '')
      .replace(/[^A-Za-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  private getOptionByControlKey(field: PhysicalFieldDefinition, controlKey: string): PhysicalFieldOption | undefined {
    return field.options?.find((option) => this.toCheckboxControlKey(option.value) === controlKey);
  }

  private getOriginalCheckboxOptionValue(field: PhysicalFieldDefinition, controlKey: string): string {
    return this.getOptionByControlKey(field, controlKey)?.value ?? controlKey;
  }

  private buildExpressionModel(): Record<string, unknown> {
    const rawModel = this.form.getRawValue() as Record<string, unknown>;
    const normalizedModel: Record<string, unknown> = {};

    for (const section of this.definition.sections) {
      for (const field of section.fields) {
        const value = rawModel[field.key];

        if (field.type === 'multicheckbox' && value && typeof value === 'object' && !Array.isArray(value)) {
          const normalizedValue: Record<string, boolean> = {};
          for (const [controlKey, checked] of Object.entries(value as Record<string, boolean>)) {
            normalizedValue[this.getOriginalCheckboxOptionValue(field, controlKey)] = checked;
          }
          normalizedModel[field.key] = normalizedValue;
          continue;
        }

        normalizedModel[field.key] = value;
      }
    }

    return normalizedModel;
  }
}
